import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { api } from "@/lib/api";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";

const SERVICES = [
  { id: "one-on-one", t: "1-to-1 PLST", d: "Dedicated one-to-one strength training with a physiotherapist." },
  { id: "semi-supervised", t: "Semi-Supervised PLST", d: "Your program. Your pace. Expert guidance when you need it." },
  { id: "physio", t: "Physiotherapy", d: "Clinical assessment, treatment and rehab." },
];
const LOCATIONS = [
  { id: "vpe", t: "Vile Parle East", s: "Mumbai" },
  { id: "thw", t: "Thane West", s: "Thane" },
  { id: "anw", t: "Andheri West — Lokhandwala", s: "Mumbai" },
];

const STEPS = ["Service", "Studio", "Your details"];

export default function BookAssessment() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    service: "", location: "",
    name: "", email: "", phone: "", concern: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const canNext =
    (step === 0 && data.service) ||
    (step === 1 && data.location) ||
    step === 2;

  async function submit() {
    if (!data.name || !data.phone) {
      toast.error("Please fill your name and phone number.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        service: data.service,
        location: data.location,
        name: data.name,
        phone: data.phone,
        concern: data.concern || "",
      };
      // Only include email if the user actually entered one (it is optional).
      if (data.email && data.email.trim()) payload.email = data.email.trim();
      await api.post("/bookings", payload);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="bg-[#F5F4EF] min-h-screen">
        <Nav />
        <div className="mx-auto max-w-3xl px-6 lg:px-12 pt-40 pb-32 text-center" data-testid="booking-success">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="w-16 h-16 rounded-full border border-[#A67C00] flex items-center justify-center mx-auto">
              <Check className="text-[#A67C00]" />
            </div>
            <h1 className="mt-8 italic font-medium text-5xl md:text-6xl tracking-tight leading-[0.95]">
              Thank you.
            </h1>
            <p className="mt-8 text-[#1E1E1E]/80 text-lg leading-relaxed">
              Our team will call or WhatsApp you within one working day to understand your requirements and schedule your visit.
            </p>
            <Link to="/" className="inline-flex items-center gap-2 mt-10 border border-[#1E1E1E] px-6 py-3 text-sm hover:bg-[#1E1E1E] hover:text-[#F5F4EF] transition-colors" data-testid="back-home">
              <ArrowLeft size={14} /> Back to home
            </Link>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#F5F4EF] min-h-screen">
      <Nav />
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12 pt-32 lg:pt-40 pb-24" data-testid="book-page">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left column */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <div className="text-xs uppercase tracking-[0.28em] text-[#8A8A8A]">
                <span className="w-8 h-px bg-[#A67C00] inline-block align-middle mr-3" />Book an Assessment
              </div>
              <h1 className="mt-6 italic font-medium text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
                Let's start with a <br />
                <span className="text-[#A67C00]">conversation.</span>
              </h1>
              <p className="mt-6 text-[#1E1E1E]/70 leading-relaxed max-w-md">
                Tell us where you are and what you'd like help with. Our team will call or WhatsApp you within one working day to schedule your visit.
              </p>

              <ol className="mt-10 space-y-4">
                {STEPS.map((s, i) => (
                  <li key={s} className={`flex items-center gap-4 text-sm ${i === step ? "text-[#1E1E1E]" : i < step ? "text-[#A67C00]" : "text-[#8A8A8A]"}`}>
                    <span className={`w-8 h-8 border ${i === step ? "border-[#1E1E1E] bg-[#1E1E1E] text-[#F5F4EF]" : i < step ? "border-[#A67C00] text-[#A67C00]" : "border-[#8A8A8A]"} flex items-center justify-center italic font-medium`}>
                      {i < step ? <Check size={14} /> : String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="tracking-wide">{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Right column */}
          <div className="lg:col-span-8">
            <div className="bg-white border border-[#1E1E1E]/10 p-8 lg:p-12 min-h-[500px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.45, ease: [0.2, 0.7, 0.2, 1] }}
                >
                  {step === 0 && (
                    <div data-testid="step-service">
                      <h2 className="font-semibold text-3xl tracking-tight">Choose a service</h2>
                      <p className="text-sm text-[#8A8A8A] mt-2">You can refine this after your conversation with us.</p>
                      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {SERVICES.map((s) => (
                          <button key={s.id} data-testid={`opt-service-${s.id}`} onClick={() => setData({ ...data, service: s.id })}
                            className={`text-left p-6 border transition-colors ${data.service === s.id ? "border-[#A67C00] bg-[#F5F4EF]" : "border-[#1E1E1E]/10 hover:border-[#1E1E1E]/40"}`}>
                            <div className="font-semibold text-xl tracking-tight">{s.t}</div>
                            <div className="text-sm text-[#1E1E1E]/70 mt-2">{s.d}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {step === 1 && (
                    <div data-testid="step-location">
                      <h2 className="font-semibold text-3xl tracking-tight">Pick a studio</h2>
                      <p className="text-sm text-[#8A8A8A] mt-2">All three centres follow the same PLST framework.</p>
                      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {LOCATIONS.map((l) => (
                          <button key={l.id} data-testid={`opt-location-${l.id}`} onClick={() => setData({ ...data, location: l.id })}
                            className={`text-left p-6 border transition-colors ${data.location === l.id ? "border-[#A67C00] bg-[#F5F4EF]" : "border-[#1E1E1E]/10 hover:border-[#1E1E1E]/40"}`}>
                            <div className="text-[10px] uppercase tracking-[0.24em] text-[#A67C00]">{l.s}</div>
                            <div className="mt-2 font-semibold text-2xl tracking-tight">{l.t}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div data-testid="step-details">
                      <h2 className="font-semibold text-3xl tracking-tight">Your details</h2>
                      <p className="text-sm text-[#8A8A8A] mt-2">We'll get back to you within one working day.</p>
                      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <Field label="Full Name" required>
                          <input required data-testid="in-name" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} className="input" />
                        </Field>
                        <Field label="Phone Number" required>
                          <input required data-testid="in-phone" value={data.phone} onChange={(e) => setData({ ...data, phone: e.target.value })} className="input" />
                        </Field>
                        <div className="sm:col-span-2">
                          <Field label="Email Address (optional)">
                            <input type="email" data-testid="in-email" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} className="input" />
                          </Field>
                        </div>
                      </div>
                      <div className="mt-6">
                        <Field label="Anything we should know? (optional)">
                          <textarea rows={4} data-testid="in-concern" value={data.concern} onChange={(e) => setData({ ...data, concern: e.target.value })} className="input resize-none" />
                          <p className="mt-2 text-xs text-[#8A8A8A]">
                            Tell us briefly about any pain, injuries, your goals, or what you'd like help with.
                          </p>
                        </Field>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Nav */}
              <div className="mt-10 pt-6 border-t border-[#1E1E1E]/10 flex items-center justify-between">
                <button
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  disabled={step === 0}
                  data-testid="btn-back"
                  className="inline-flex items-center gap-2 text-sm text-[#1E1E1E]/70 hover:text-[#1E1E1E] disabled:opacity-30"
                >
                  <ArrowLeft size={14} /> Back
                </button>
                {step < 2 ? (
                  <button
                    onClick={() => canNext && setStep((s) => s + 1)}
                    disabled={!canNext}
                    data-testid="btn-next"
                    className="inline-flex items-center gap-2 bg-[#1E1E1E] text-[#F5F4EF] px-6 py-3 text-sm hover:bg-[#A67C00] transition-colors duration-500 disabled:opacity-40"
                  >
                    Continue <ArrowRight size={14} />
                  </button>
                ) : (
                  <button
                    onClick={submit}
                    disabled={busy}
                    data-testid="btn-submit"
                    className="inline-flex items-center gap-2 bg-[#1E1E1E] text-[#F5F4EF] px-6 py-3 text-sm hover:bg-[#A67C00] transition-colors duration-500 disabled:opacity-60"
                  >
                    {busy ? "Sending…" : "Send Enquiry"} <ArrowRight size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <style>{`
        .input { width:100%; background:transparent; border:0; border-bottom:1px solid rgba(30,30,30,0.2); padding:12px 0; font-size:15px; color:#1E1E1E; transition:border-color .3s; }
        .input:focus { outline:none; border-color:#A67C00; }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.24em] text-[#8A8A8A]">{label}{required && " *"}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
